import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  MessageCircle, 
  Send, 
  Sparkles, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  X,
  Minimize2,
  Maximize2,
  Bot,
  User,
  Zap,
  RefreshCw,
  Share2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useCurrency } from "../contexts/CurrencyContext";
import { cn } from "../lib/utils";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_QUESTIONS = [
  { icon: TrendingUp, text: "What's my most profitable product?", color: "text-emerald-600" },
  { icon: TrendingDown, text: "Where am I losing money?", color: "text-red-600" },
  { icon: Sparkles, text: "Should I raise prices?", color: "text-violet-600" },
  { icon: Zap, text: "Which campaigns should I scale?", color: "text-amber-600" },
  { icon: TrendingUp, text: "How to increase repeat purchases?", color: "text-emerald-600" },
  { icon: Sparkles, text: "Give me 3 actions to boost profit", color: "text-violet-600" },
];

export default function AICopilot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [quickInsights, setQuickInsights] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { formatPrice } = useCurrency();

  // Load quick insights on mount
  useEffect(() => {
    if (!isHidden) {
      fetchQuickInsights();
    }
  }, [isHidden]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const fetchQuickInsights = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-copilot/quick-insights`);
      const data = await response.json();
      setQuickInsights(data);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    }
  };

  // If hidden, don't render anything
  if (isHidden) {
    return null;
  }

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    // Add user message
    const userMessage = { role: "user", content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai-copilot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: "web_chat" })
      });
      
      const data = await response.json();
      
      // Add AI response
      const aiMessage = { 
        role: "assistant", 
        content: data.response, 
        insights: data.insights,
        timestamp: new Date() 
      };
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  // Floating button when closed - positioned higher to avoid platform badge overlap
  if (!isOpen) {
    return (
      <div className="fixed bottom-24 right-6 z-[9999]">
        {/* Insight badge - clickable to open chat */}
        {quickInsights?.insights?.[0] && (
          <div 
            onClick={() => setIsOpen(true)}
            className="absolute -top-20 right-0 hidden xl:block bg-white rounded-lg shadow-lg p-3 w-[260px] cursor-pointer hover:shadow-xl transition-shadow border border-slate-100 overflow-hidden"
          >
            <div className="flex items-start gap-2">
              <span className="text-lg">{quickInsights.insights[0].icon}</span>
              <div>
                <p className="text-xs font-medium text-slate-900">{quickInsights.insights[0].title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{quickInsights.insights[0].message}</p>
              </div>
            </div>
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45 border-r border-b border-slate-100"></div>
          </div>
        )}
        
        {/* Pulsing Ring Animation */}
        <div className="absolute inset-0 rounded-full animate-ping bg-violet-400 opacity-25" />
        <div className="absolute -inset-1 rounded-full animate-pulse bg-gradient-to-r from-violet-400 to-purple-400 opacity-30" />
        
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); setIsHidden(true); }}
          className="absolute -top-2 -right-2 w-6 h-6 bg-slate-700 hover:bg-slate-800 rounded-full flex items-center justify-center shadow-lg z-10"
          title="Hide AI Copilot"
        >
          <X className="w-3 h-3 text-white" />
        </button>
        
        <Button
          onClick={() => setIsOpen(true)}
          className="relative w-16 h-16 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-xl shadow-violet-500/40 hover:shadow-2xl hover:shadow-violet-500/50 transition-all duration-200 hover:scale-110"
          data-testid="ai-copilot-btn"
        >
          <Sparkles className="w-7 h-7 text-white" />
        </Button>
        
        {/* "Ask AI" Label */}
        <div className="absolute -left-20 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
          Ask AI CFO
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
        </div>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-24 right-6 z-[9999]">
        <div 
          className="bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-3 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => setIsMinimized(false)}
        >
          <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <span className="font-medium text-slate-900">AI Profit Copilot</span>
          <Maximize2 className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    );
  }

  // Full chat interface
  return (
    <div className="fixed bottom-24 right-6 z-[9999] w-[400px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white">AI Profit Copilot</h3>
            <p className="text-xs text-white/80">Your Shopify profit expert</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => setIsMinimized(true)}
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="space-y-4">
            {/* Welcome message */}
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-violet-100 to-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-violet-600" />
              </div>
              <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4 max-w-[85%]">
                <p className="text-sm text-slate-700">
                  👋 Hi! I'm your AI Profit Copilot. I analyze your Shopify store and ad performance to help you <strong>maximize profit</strong>.
                </p>
                <p className="text-sm text-slate-600 mt-2">
                  Ask me anything about your ads, products, or profitability!
                </p>
              </div>
            </div>

            {/* Quick questions */}
            <div className="space-y-2">
              <p className="text-xs text-slate-500 px-2">Quick questions:</p>
              {QUICK_QUESTIONS.map((q, i) => (
                <button
                  key={q?.id ?? q?.opportunity_id ?? q?.action_id ?? q?.shopify_id ?? q?.sku ?? q?.campaign_id ?? q?.ad_id ?? q?.product_id ?? q?.customer_id ?? q?.handle ?? q?.slug ?? q?.key ?? q?.name ?? `row-${i}`}
                  onClick={() => handleQuickQuestion(q.text)}
                  className="w-full flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50 transition-colors text-left"
                >
                  <q.icon className={cn("w-5 h-5", q.color)} />
                  <span className="text-sm text-slate-700">{q.text}</span>
                </button>
              ))}
            </div>

            {/* Quick insights preview */}
            {quickInsights?.insights?.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500 px-2">Current insights:</p>
                {quickInsights.insights.slice(0, 2).map((insight, i) => (
                  <div key={insight?.id ?? insight?.opportunity_id ?? insight?.action_id ?? insight?.shopify_id ?? insight?.sku ?? insight?.campaign_id ?? insight?.ad_id ?? insight?.product_id ?? insight?.customer_id ?? insight?.handle ?? insight?.slug ?? insight?.key ?? insight?.name ?? `row-${i}`} className={cn(
                    "p-3 rounded-xl border",
                    insight.type === "scale" && "bg-emerald-50 border-emerald-200",
                    insight.type === "pause" && "bg-red-50 border-red-200",
                    insight.type === "warning" && "bg-amber-50 border-amber-200",
                    insight.type === "success" && "bg-emerald-50 border-emerald-200"
                  )}>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{insight.icon}</span>
                      <div>
                        <p className="text-xs font-semibold text-slate-900">{insight.title}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{insight.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Generate Weekly Report Button */}
            <div className="pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/weekly-report');
                }}
                className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl transition-all"
              >
                <Share2 className="w-4 h-4" />
                <span className="text-sm font-medium">Generate Shareable Report</span>
              </button>
              <p className="text-xs text-slate-400 text-center mt-2">
                Create a visual report to share with your team
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div key={msg?.id ?? msg?.opportunity_id ?? msg?.action_id ?? msg?.shopify_id ?? msg?.sku ?? msg?.campaign_id ?? msg?.ad_id ?? msg?.product_id ?? msg?.customer_id ?? msg?.handle ?? msg?.slug ?? msg?.key ?? msg?.name ?? `row-${i}`} className={cn("flex gap-3", msg.role === "user" && "flex-row-reverse")}>
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  msg.role === "user" 
                    ? "bg-slate-700" 
                    : "bg-gradient-to-r from-violet-100 to-purple-100"
                )}>
                  {msg.role === "user" 
                    ? <User className="w-4 h-4 text-white" />
                    : <Bot className="w-4 h-4 text-violet-600" />
                  }
                </div>
                <div className={cn(
                  "rounded-2xl p-4 max-w-[85%]",
                  msg.role === "user" 
                    ? "bg-slate-700 text-white rounded-tr-none" 
                    : "bg-slate-100 text-slate-700 rounded-tl-none"
                )}>
                  {msg.role === "assistant" ? (
                    <div className="prose prose-sm prose-slate max-w-none">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-violet-100 to-purple-100 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-violet-600" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-violet-600 animate-spin" />
                    <span className="text-sm text-slate-600">Analyzing your data...</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask about your profits..."
            className="flex-1 px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:border-violet-400 text-sm"
            disabled={isLoading}
            data-testid="copilot-input"
          />
          <Button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            data-testid="copilot-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          Powered by AI · Using your real store data
        </p>
      </form>
    </div>
  );
}
